import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { EyeIcon } from '@heroicons/react/24/outline'
import Modal from '@components/Modal'

interface MessagePreviewModalProps {
  show: boolean
  onClose: () => void
  language: 'en' | 'es'
}

export default function MessagePreviewModal({ show, onClose, language }: MessagePreviewModalProps) {
  const { t, i18n } = useTranslation()

  // Preload the target language bundle so getFixedT works for non-active languages
  const previewLang = language === 'es' ? 'es' : 'en-US'
  const [langLoaded, setLangLoaded] = useState(false)

  useEffect(() => {
    if (show) {
      i18n.loadLanguages(previewLang).then(() => setLangLoaded(true))
    }
  }, [show, previewLang, i18n])

  const tPreview = (key: string) => {
    return i18n.getFixedT(previewLang)(key)
  }

  const smsBody = langLoaded ? tPreview('content.components.message_preview.sms_body') : ''
  const emailSubject = langLoaded ? tPreview('content.components.message_preview.email_subject') : ''
  const emailBody = langLoaded ? tPreview('content.components.message_preview.email_body') : ''

  return (
    <Modal
      variant='cta'
      show={show}
      title={t('content.components.message_preview.title')}
      icon={<EyeIcon aria-hidden='true' className='size-6 text-cta' />}
      onClose={onClose}
    >
      <div className='space-y-4'>
        {/* SMS Preview */}
        <div>
          <h4 className='text-sm font-semibold text-gray-700 mb-1'>
            {t('content.components.message_preview.sms_label')}
          </h4>
          <div className='rounded-lg bg-gray-100 p-3 text-sm text-gray-800 whitespace-pre-line'>{smsBody}</div>
        </div>

        {/* Email Preview */}
        <div>
          <h4 className='text-sm font-semibold text-gray-700 mb-1'>
            {t('content.components.message_preview.email_label')}
          </h4>
          <div className='rounded-lg bg-gray-100 p-3 text-sm text-gray-800'>
            <div className='font-semibold mb-2'>{emailSubject}</div>
            <div className='whitespace-pre-line'>{emailBody}</div>
          </div>
        </div>

        {/* Note */}
        <p className='text-xs text-gray-400 italic'>{t('content.components.message_preview.note')}</p>
      </div>
    </Modal>
  )
}
